// Copyright 2026, Imprivata, Inc.  All rights reserved.

import {
  AppError as AuthnAppError,
  ErrorCode as AuthnErrorCode,
  EventType,
} from '@imprivata-cloud/authn';
import {
  CAMERA_ERROR_CODES,
  type CameraErrorCode,
  type CameraNotification,
  AppError as CoreAppError,
  cameraErrorNotification,
  type FaceCeremony,
  type TranslateKey,
} from '@imprivata-cloud/core';

/**
 * Host-side camera adapters — what an event means for the flow, and what copy the
 * user reads. Both translate authn's error vocabulary into core's.
 */

const AUTHN_ERROR_CODES: ReadonlySet<string> = new Set(
  Object.values(AuthnErrorCode),
);

export const isAuthnErrorCode = (
  code: string | undefined,
): code is AuthnErrorCode => code !== undefined && AUTHN_ERROR_CODES.has(code);

/**
 * Camera disposition is a function of (event, code) alone. Decided here once, not
 * per container: deciding it inline left tenant-lookup silent on a denied camera.
 */
export type CameraEventOutcome =
  /** Recoverable: describe the condition and leave the flow in place. */
  | { kind: 'banner'; code: AuthnErrorCode }
  /** Unrecoverable: the camera never initialized, so the flow cannot continue. */
  | { kind: 'terminal'; code: AuthnErrorCode }
  /** The condition resolved itself; retire any camera message. */
  | { kind: 'clear' }
  /** Not a camera event, or a payload with no code to report. */
  | { kind: 'ignore' };

/** The events `cameraEventOutcome` decides. Anything else yields `ignore`. */
export const CAMERA_EVENTS: ReadonlySet<EventType> = new Set([
  EventType.CAMERA_ERROR,
  EventType.CAMERA_IN_USE,
  EventType.PERMISSION_REVOKED,
  EventType.DEVICE_DISCONNECTED,
  EventType.PERMISSION_GRANTED,
  EventType.DEVICE_RECONNECTED,
]);

/**
 * Camera payloads are `AppError | string | never` by event, but the host receives
 * the whole `EventPayload['data']` union — so accept it wide and match on shape
 * rather than making every callsite assert.
 */
const codeFromPayload = (data: unknown): string | undefined => {
  if (typeof data === 'string') return data;
  return data instanceof AuthnAppError ? data.code : undefined;
};

export const cameraEventOutcome = (
  event: EventType,
  data?: unknown,
): CameraEventOutcome => {
  switch (event) {
    case EventType.CAMERA_ERROR:
    case EventType.CAMERA_IN_USE:
    case EventType.PERMISSION_REVOKED: {
      const code = codeFromPayload(data);
      if (!isAuthnErrorCode(code)) return { kind: 'ignore' };
      // The camera never came up, so no in-flow message can help: the one camera
      // condition that fails the whole authentication.
      return code === AuthnErrorCode.DEVICE_INITIALIZATION_TIMEOUT
        ? { kind: 'terminal', code }
        : { kind: 'banner', code };
    }

    // Losing an acquired device reports no code on some paths; name the condition
    // from the event instead.
    case EventType.DEVICE_DISCONNECTED: {
      const code = codeFromPayload(data);
      return {
        kind: 'banner',
        code: isAuthnErrorCode(code)
          ? code
          : AuthnErrorCode.DEVICE_DISCONNECTED,
      };
    }

    case EventType.PERMISSION_GRANTED:
    case EventType.DEVICE_RECONNECTED:
      return { kind: 'clear' };

    default:
      return { kind: 'ignore' };
  }
};

// `core`'s own `isCameraErrorCode` narrows against its `ErrorCode` enum, which
// authn's structurally-identical enum is not assignable to. The values are shared
// (`CAMERA_ERROR_CODES` is the source of truth both packages re-export), so match
// on those and let the guard narrow.
// The element annotation is deliberate, not redundant: it pins the enumeration to
// core's own union at the construction site, so an upstream change from an object
// map to a `Set`/array (where `Object.values` would yield the wrong members, or
// none) is a compile error here rather than an empty set that silently routes every
// camera code down the non-camera path.
const CAMERA_CODES: ReadonlySet<string> = new Set<CameraErrorCode>(
  Object.values(CAMERA_ERROR_CODES),
);

const isCameraErrorCode = (code?: string): code is CameraErrorCode =>
  code !== undefined && CAMERA_CODES.has(code);

/**
 * The display-ready notification for a camera failure, or `undefined` when the
 * code is not a camera condition — in which case the caller keeps its own copy
 * path. `title`, `message` and `level` come from one map, so a renderer needs no
 * second table for severity.
 *
 * Copy comes from `core`'s `CAMERA_NOTIFICATIONS` rather than the host's own
 * `errors.<code>` convention because it is a function of (code, ceremony): a
 * blocked camera reads "required for face authentication" while authenticating and
 * "required to enroll your face" while enrolling. No map keyed on the code alone
 * can express that, and `core` owns the map authn ships translations for.
 *
 * `t` resolves keys at the bundle root (`errors.*`), matching the keys `core`
 * names; a container with its own translation root passes a translator that
 * reroots them.
 */
export const resolveCameraNotification = (
  t: TranslateKey,
  ceremony: FaceCeremony,
  code?: string,
): CameraNotification | undefined => {
  if (!isCameraErrorCode(code)) {
    return undefined;
  }

  // `cameraErrorNotification` takes core's `AppError`; the host holds authn's, a
  // nominally distinct class carrying the same code. The code is the copy's only
  // input, so rebuild it as core's.
  return cameraErrorNotification(t, ceremony, new CoreAppError({ code }));
};
